import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, Calendar, MessageSquare, FileText, 
  BarChart, TrendingUp
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState({
    total_customers: 0,
    total_reservations: 0,
    reservations_this_month: 0,
    pending_reservations: 0,
    recent_reservations: [],
    reservations_by_type: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const [customersRes, reservationsRes] = await Promise.all([
        axios.get(`${API}/admin/customers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/reservations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const customers = customersRes.data;
      const reservations = reservationsRes.data;

      // Calculate metrics
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      const reservationsThisMonth = reservations.filter(r => {
        const resDate = new Date(r.date);
        return resDate.getMonth() === currentMonth && resDate.getFullYear() === currentYear;
      }).length;

      const pendingReservations = reservations.filter(r => r.status === 'pending').length;

      const typeCount = {};
      reservations.forEach(r => {
        typeCount[r.experience_type] = (typeCount[r.experience_type] || 0) + 1;
      });

      const reservationsByType = Object.entries(typeCount).map(([type, count]) => ({
        type,
        count
      }));

      setMetrics({
        total_customers: customers.length,
        total_reservations: reservations.length,
        reservations_this_month: reservationsThisMonth,
        pending_reservations: pendingReservations,
        recent_reservations: reservations.slice(0, 5),
        reservations_by_type: reservationsByType
      });
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 rounded-full animate-spin" 
             style={{borderColor: '#D4AF37', borderTopColor: 'transparent'}}></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Dashboard</h1>
        <p className="text-gray-600">Bienvenido de vuelta, {user?.username}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Clientes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-lg" style={{background: 'rgba(212, 175, 55, 0.1)'}}>
              <Users className="h-6 w-6" style={{color: '#D4AF37'}} />
            </div>
            <span className="text-sm text-gray-500">Total</span>
          </div>
          <h3 className="text-3xl font-bold text-gray-800 mb-1">{metrics.total_customers}</h3>
          <p className="text-gray-600">Clientes</p>
        </motion.div>

        {/* Total Reservas */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-lg bg-blue-50">
              <Calendar className="h-6 w-6 text-blue-600" />
            </div>
            <span className="text-sm text-gray-500">Total</span>
          </div>
          <h3 className="text-3xl font-bold text-gray-800 mb-1">{metrics.total_reservations}</h3>
          <p className="text-gray-600">Reservas</p>
        </motion.div>

        {/* Reservas del Mes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-lg bg-green-50">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <span className="text-sm text-gray-500">Este mes</span>
          </div>
          <h3 className="text-3xl font-bold text-gray-800 mb-1">{metrics.reservations_this_month}</h3>
          <p className="text-gray-600">Reservas mes</p>
        </motion.div>

        {/* Reservas Pendientes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-lg bg-orange-50">
              <Calendar className="h-6 w-6 text-orange-600" />
            </div>
            <span className="text-sm text-gray-500">Pendientes</span>
          </div>
          <h3 className="text-3xl font-bold text-gray-800 mb-1">{metrics.pending_reservations}</h3>
          <p className="text-gray-600">Por confirmar</p>
        </motion.div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Reservations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
        >
          <h3 className="text-xl font-bold text-gray-800 mb-4">Últimas Reservas</h3>
          <div className="space-y-3">
            {metrics.recent_reservations.length > 0 ? (
              metrics.recent_reservations.map((reservation, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-800">{reservation.customer_name}</p>
                    <p className="text-sm text-gray-500">{reservation.experience_type}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-800">{reservation.date}</p>
                    <p className="text-sm text-gray-500">{reservation.time}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No hay reservas recientes</p>
            )}
          </div>
        </motion.div>

        {/* Reservations by Type */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
        >
          <h3 className="text-xl font-bold text-gray-800 mb-4">Reservas por Tipo</h3>
          <div className="space-y-3">
            {metrics.reservations_by_type.length > 0 ? (
              metrics.reservations_by_type.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600 font-medium">{item.type}</span>
                  <span className="font-bold text-gray-800 px-3 py-1 rounded-full" 
                        style={{background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37'}}>
                    {item.count}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No hay datos disponibles</p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        <button
          onClick={() => navigate('/admin/news')}
          className="p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-300 transition-all text-left group"
        >
          <FileText className="h-8 w-8 mb-3" style={{color: '#D4AF37'}} />
          <h3 className="text-lg font-bold text-gray-800 mb-1">Gestionar Noticias</h3>
          <p className="text-sm text-gray-600">Crear y editar noticias del sitio web</p>
        </button>

        <button
          onClick={() => navigate('/admin/reservations')}
          className="p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-300 transition-all text-left group"
        >
          <Calendar className="h-8 w-8 mb-3 text-blue-600" />
          <h3 className="text-lg font-bold text-gray-800 mb-1">Ver Reservas</h3>
          <p className="text-sm text-gray-600">Ver y gestionar todas las reservas</p>
        </button>

        <button
          onClick={() => navigate('/admin/customers')}
          className="p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-300 transition-all text-left group"
        >
          <Users className="h-8 w-8 mb-3 text-green-600" />
          <h3 className="text-lg font-bold text-gray-800 mb-1">Ver Clientes</h3>
          <p className="text-sm text-gray-600">Gestionar base de datos de clientes</p>
        </button>
      </motion.div>
    </div>
  );
};

export default AdminDashboard;
